import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../../../../../components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,  
  TableHead,
  TableHeader,
  TableRow,
} from "../../../../../components/ui/table";
import { ThumbsUp, ArrowRight, ChevronLeft, ThumbsDown } from 'lucide-react';
import { Button } from '../../../../../components/ui/button';
import type { WeldData } from './WeldDataTable';

// Define parameter limits
const PARAMETER_LIMITS = {
  voltage: { min: 20, max: 30 },
  current: { min: 150, max: 250 },
  travelSpeed: { min: 100, max: 300 },
  heat: { min: 1.0, max: 2.0 },
  trueEnergy: { min: 2.5, max: 4.5 },
  oscillationWidth: { min: 2.0, max: 4.0 },
  wireSpeed: { min: 150, max: 450 }
};

interface WeldPass {
  name: string;
  status: 'Completed' | 'Not Started' | 'In Progress' | 'Rejected';
  welderId: string;
  duration: string;
  startTime?: string;
  endTime?: string;
  voltage?: { min: number; avg: number; max: number };
  current?: { min: number; avg: number; max: number };
  travelSpeed?: { min: number; avg: number; max: number };
  heat?: { min: number; avg: number; max: number };
  trueEnergy?: { min: number; avg: number; max: number };
  oscillationWidth?: { min: number; avg: number; max: number };
  wireSpeed?: { min: number; avg: number; max: number };
}

interface WeldPassListProps {
  isOpen: boolean;
  onClose: () => void;
  weld: WeldData;
  assemblyNumber: string;
}

export const WeldPassList: React.FC<WeldPassListProps> = ({
  isOpen,
  onClose,
  weld,
  assemblyNumber
}) => {
  // Helper function to check if a parameter is within limits
  const isWithinLimits = (value: number, paramName: keyof typeof PARAMETER_LIMITS): boolean => {
    const limits = PARAMETER_LIMITS[paramName];
    return value >= limits.min && value <= limits.max;
  };

  // Helper function to check if all parameters are within limits
  const areAllParamsWithinLimits = (pass: WeldPass): boolean => {
    if (pass.status !== 'Completed') return true;

    const checkParam = (param: { min: number; avg: number; max: number } | undefined, paramName: keyof typeof PARAMETER_LIMITS): boolean => {
      if (!param) return true;
      return isWithinLimits(param.min, paramName) && 
             isWithinLimits(param.avg, paramName) && 
             isWithinLimits(param.max, paramName);
    };

    return checkParam(pass.voltage, 'voltage') &&
           checkParam(pass.current, 'current') &&
           checkParam(pass.travelSpeed, 'travelSpeed') &&
           checkParam(pass.heat, 'heat') &&
           checkParam(pass.trueEnergy, 'trueEnergy') &&
           checkParam(pass.oscillationWidth, 'oscillationWidth') &&
           checkParam(pass.wireSpeed, 'wireSpeed');
  };

  // Helper function to render min/avg/max values with proper formatting
  const renderMinAvgMax = (
    values?: { min: number; avg: number; max: number }, 
    decimals: number = 1, 
    paramName?: keyof typeof PARAMETER_LIMITS
  ) => {
    if (!values && paramName === 'wireSpeed') {
      const baseSpeed = Math.floor(Math.random() * (400 - 200 + 1)) + 200;
      const variation = 50;
      const min = baseSpeed - Math.floor(Math.random() * variation);
      const max = baseSpeed + Math.floor(Math.random() * variation);
      const isMinValid = isWithinLimits(min, 'wireSpeed');
      const isAvgValid = isWithinLimits(baseSpeed, 'wireSpeed');
      const isMaxValid = isWithinLimits(max, 'wireSpeed');

      return (
        <div className="grid grid-cols-3 gap-1 bg-gray-50 rounded-md h-full">
          <div className="flex items-center justify-center h-full">
            <div className={`flex items-center justify-center text-blue-600 h-full w-full ${!isMinValid ? 'bg-red-100 rounded-lg mx-2' : ''}`}>
              {min.toFixed(0)}
            </div>
          </div>
          <div className="flex items-center justify-center h-full">
            <div className={`flex items-center justify-center font-medium h-full w-full ${!isAvgValid ? 'bg-red-100 rounded-lg mx-2' : 'bg-white'}`}>
              {baseSpeed.toFixed(0)}
            </div>
          </div>
          <div className="flex items-center justify-center h-full">
            <div className={`flex items-center justify-center text-blue-600 h-full w-full ${!isMaxValid ? 'bg-red-100 rounded-lg mx-2' : ''}`}>
              {max.toFixed(0)}
            </div>
          </div>
        </div>
      );
    }
    if (!values) return (
      <div className="grid grid-cols-3 gap-1 h-full">
        <div className="text-gray-400 flex items-center justify-center h-full">-</div>
        <div className="text-gray-400 flex items-center justify-center h-full">-</div>
        <div className="text-gray-400 flex items-center justify-center h-full">-</div>
      </div>
    );

    const isMinValid = !paramName || isWithinLimits(values.min, paramName);
    const isAvgValid = !paramName || isWithinLimits(values.avg, paramName);
    const isMaxValid = !paramName || isWithinLimits(values.max, paramName);

    return (
      <div className="grid grid-cols-3 gap-1 bg-gray-50 rounded-md h-full">
        <div className="flex items-center justify-center h-full">
          <div className={`flex items-center justify-center text-blue-600 h-full w-full ${!isMinValid ? 'bg-red-100 rounded-lg mx-2' : ''}`}>
            {values.min.toFixed(decimals)}
          </div>
        </div>
        <div className="flex items-center justify-center h-full">
          <div className={`flex items-center justify-center font-medium h-full w-full ${!isAvgValid ? 'bg-red-100 rounded-lg mx-2' : 'bg-white'}`}>
            {values.avg.toFixed(decimals)}
          </div>
        </div>
        <div className="flex items-center justify-center h-full">
          <div className={`flex items-center justify-center text-blue-600 h-full w-full ${!isMaxValid ? 'bg-red-100 rounded-lg mx-2' : ''}`}>
            {values.max.toFixed(decimals)}
          </div>
        </div>
      </div>
    );
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-[100vw] w-screen h-screen max-h-screen p-0 gap-0">
        <div className="flex flex-col h-full">
          <DialogHeader className="px-6 py-4 border-b flex flex-row items-center bg-white">
            <Button
              variant="ghost"
              size="sm"
              onClick={onClose}
              className="mr-4 hover:bg-gray-100"
            >
              <ChevronLeft className="h-5 w-5" />
              <span className="ml-1">Back</span>
            </Button>
            <div>
              <DialogTitle className="text-2xl font-bold">
                Weld Passes - {weld.weldNumber} ({weld.weldIdentifier})
              </DialogTitle>
              <p className="text-sm text-gray-500 mt-1">Assembly: {assemblyNumber}</p>
            </div>
          </DialogHeader>
          
          <div className="flex-1 overflow-hidden">
            <div className="h-full relative">
              <div className="absolute inset-0 overflow-auto">
                <div className="inline-flex min-w-full">
                  {/* Fixed columns */}
                  <div className="sticky left-0 z-20 bg-white shadow-lg">
                    <table className="border-collapse">
                      <thead className="sticky top-0 bg-white z-10">
                        <tr className="border-b h-[72px] bg-gray-50">
                          <th className="w-[150px] px-4 text-left text-sm font-medium text-gray-600">Pass Name</th>
                          <th className="w-[120px] px-4 text-center text-sm font-medium text-gray-600">Bug Type</th>
                          <th className="w-[100px] px-4 text-center text-sm font-medium text-gray-600">Status</th>
                          <th className="w-[120px] px-4 text-center text-sm font-medium text-gray-600">Welder ID</th>
                          <th className="w-[160px] px-4 text-center text-sm font-medium text-gray-600">Start Time</th>
                          <th className="w-[160px] px-4 text-center text-sm font-medium text-gray-600">End Time</th>
                          <th className="w-[100px] px-4 text-center text-sm font-medium text-gray-600 border-r">Duration</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(weld.passes || []).flatMap((pass, index) => {
                          // Calculate times and duration
                          const getTimeInfo = (isCounterClockwise: boolean) => {
                            if (pass.status === 'Not Started') {
                              return {
                                startTime: '-',
                                endTime: '-',
                                duration: '-'
                              };
                            }

                            const baseHour = isCounterClockwise ? 9 : 8;
                            const startMinutes = index * 30; // 30 minutes gap between passes
                            
                            // Generate random duration between 2 and 10 minutes
                            const durationMinutes = Math.floor(Math.random() * (10 - 2 + 1)) + 2;
                            const durationSeconds = Math.floor(Math.random() * 60); // Random seconds
                            
                            const startDate = new Date(2024, 2, 15 + index); // March is 2 (0-based)
                            startDate.setHours(baseHour, startMinutes, 0);
                            
                            const endDate = new Date(startDate);
                            endDate.setMinutes(startDate.getMinutes() + durationMinutes);
                            endDate.setSeconds(durationSeconds);

                            const formatDate = (date: Date) => {
                              const day = String(date.getDate()).padStart(2, '0');
                              const hours = String(date.getHours()).padStart(2, '0');
                              const minutes = String(date.getMinutes()).padStart(2, '0');
                              const year = date.getFullYear();
                              // Show year in YY format
                              const shortYear = year.toString().slice(-2);
                              return `${day}-Mar'${shortYear} ${hours}:${minutes}`;
                            };

                            // Format duration to be more concise
                            const formatDuration = (mins: number, secs: number) => {
                              if (mins === 0) {
                                return `${secs}s`;
                              }
                              return secs === 0 ? `${mins}m` : `${mins}m ${secs}s`;
                            };

                            return {
                              startTime: formatDate(startDate),
                              endTime: pass.status === 'In Progress' ? '-' : formatDate(endDate),
                              duration: pass.status === 'Completed' ? formatDuration(durationMinutes, durationSeconds) : '-'
                            };
                          };

                          const clockwiseTime = getTimeInfo(false);
                          const counterClockwiseTime = getTimeInfo(true);

                          return [
                            <tr key={`${pass.name}-clockwise`} className={`h-[52px] bg-white ${index % 2 === 0 ? 'bg-opacity-100' : 'bg-opacity-50'}`}>
                              <td className="px-4 font-medium">{pass.name}</td>
                              <td className="px-4 text-center text-gray-600">Clockwise</td>
                              <td className="px-4 text-center">
                                {pass.status === 'Completed' && (
                                  areAllParamsWithinLimits(pass) ? 
                                    <ThumbsUp className="h-4 w-4 text-green-600 inline" /> :
                                    <ThumbsDown className="h-4 w-4 text-red-600 inline" />
                                )}
                                {pass.status === 'In Progress' && <div className="text-yellow-600 text-sm">In Progress</div>}
                                {pass.status === 'Not Started' && <div className="text-gray-400 text-sm">Not Started</div>}
                                {pass.status === 'Rejected' && <div className="text-red-600 text-sm">Rejected</div>}
                              </td>
                              <td className="px-4 text-center text-gray-600">{pass.welderId}</td>
                              <td className="px-4 text-center text-gray-600">{clockwiseTime.startTime}</td>
                              <td className="px-4 text-center text-gray-600">{clockwiseTime.endTime}</td>
                              <td className="px-4 text-center border-r text-gray-600">{clockwiseTime.duration}</td>
                            </tr>,
                            <tr key={`${pass.name}-counterclockwise`} className={`h-[52px] bg-white ${index % 2 === 0 ? 'bg-opacity-100' : 'bg-opacity-50'} border-b`}>
                              <td className="px-4 font-medium">{pass.name}</td>
                              <td className="px-4 text-center text-gray-600">Counterclockwise</td>
                              <td className="px-4 text-center">
                                {pass.status === 'Completed' && (
                                  areAllParamsWithinLimits(pass) ? 
                                    <ThumbsUp className="h-4 w-4 text-green-600 inline" /> :
                                    <ThumbsDown className="h-4 w-4 text-red-600 inline" />
                                )}
                                {pass.status === 'In Progress' && <div className="text-yellow-600 text-sm">In Progress</div>}
                                {pass.status === 'Not Started' && <div className="text-gray-400 text-sm">Not Started</div>}
                                {pass.status === 'Rejected' && <div className="text-red-600 text-sm">Rejected</div>}
                              </td>
                              <td className="px-4 text-center text-gray-600">{pass.welderId}</td>
                              <td className="px-4 text-center text-gray-600">{counterClockwiseTime.startTime}</td>
                              <td className="px-4 text-center text-gray-600">{counterClockwiseTime.endTime}</td>
                              <td className="px-4 text-center border-r text-gray-600">{counterClockwiseTime.duration}</td>
                            </tr>
                          ];
                        })}
                      </tbody>
                    </table>
                  </div>

                  {/* Scrollable parameters section */}
                  <div className="overflow-x-auto">
                    <table className="border-collapse">
                      <thead className="sticky top-0 bg-white z-10">
                        <tr className="border-b h-[72px] bg-gray-50">
                          <th className="px-4 min-w-[200px] group">
                            <div className="h-[40px] flex items-center justify-center text-center font-medium text-sm text-gray-600">
                              <div>
                                Voltage<br />(V)
                              </div>
                            </div>
                            <div className="grid grid-cols-3 text-xs font-normal text-center text-gray-500">
                              <div>Min</div>
                              <div>Avg</div>
                              <div>Max</div>
                            </div>
                          </th>
                          <th className="px-4 min-w-[200px] border-l border-gray-200">
                            <div className="h-[40px] flex items-center justify-center text-center font-medium text-sm text-gray-600">
                              <div>
                                Current<br />(A)
                              </div>
                            </div>
                            <div className="grid grid-cols-3 text-xs font-normal text-center text-gray-500">
                              <div>Min</div>
                              <div>Avg</div>
                              <div>Max</div>
                            </div>
                          </th>
                          <th className="px-4 min-w-[200px] border-l border-gray-200">
                            <div className="h-[40px] flex items-center justify-center text-center font-medium text-sm text-gray-600">
                              <div>
                                Travel Speed<br />(mm/min)
                              </div>
                            </div>
                            <div className="grid grid-cols-3 text-xs font-normal text-center text-gray-500">
                              <div>Min</div>
                              <div>Avg</div>
                              <div>Max</div>
                            </div>
                          </th>
                          <th className="px-4 min-w-[200px] border-l border-gray-200">
                            <div className="h-[40px] flex items-center justify-center text-center font-medium text-sm text-gray-600">
                              <div>
                                Heat<br />(kJ/mm)
                              </div>
                            </div>
                            <div className="grid grid-cols-3 text-xs font-normal text-center text-gray-500">
                              <div>Min</div>
                              <div>Avg</div>
                              <div>Max</div>
                            </div>
                          </th>
                          <th className="px-4 min-w-[200px] border-l border-gray-200">
                            <div className="h-[40px] flex items-center justify-center text-center font-medium text-sm text-gray-600">
                              <div>
                                True Energy<br />(kJ)
                              </div>
                            </div>
                            <div className="grid grid-cols-3 text-xs font-normal text-center text-gray-500">
                              <div>Min</div>
                              <div>Avg</div>
                              <div>Max</div>
                            </div>
                          </th>
                          <th className="px-4 min-w-[200px] border-l border-gray-200">
                            <div className="h-[40px] flex items-center justify-center text-center font-medium text-sm text-gray-600">
                              <div>
                                Oscillation Width<br />(mm)
                              </div>
                            </div>
                            <div className="grid grid-cols-3 text-xs font-normal text-center text-gray-500">
                              <div>Min</div>
                              <div>Avg</div>
                              <div>Max</div>
                            </div>
                          </th>
                          <th className="px-4 min-w-[200px] border-l border-gray-200">
                            <div className="h-[40px] flex items-center justify-center text-center font-medium text-sm text-gray-600">
                              <div>
                                Wire Speed<br />(mm/min)
                              </div>
                            </div>
                            <div className="grid grid-cols-3 text-xs font-normal text-center text-gray-500">
                              <div>Min</div>
                              <div>Avg</div>
                              <div>Max</div>
                            </div>
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {(weld.passes || []).flatMap((pass, index) => [
                          <tr key={`${pass.name}-clockwise-params`} className={`h-[52px] ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}>
                            <td className="px-4 py-2 h-[52px]">{renderMinAvgMax(pass.voltage, 1, 'voltage')}</td>
                            <td className="px-4 py-2 border-l border-gray-200 h-[52px]">{renderMinAvgMax(pass.current, 0, 'current')}</td>
                            <td className="px-4 py-2 border-l border-gray-200 h-[52px]">{renderMinAvgMax(pass.travelSpeed, 0, 'travelSpeed')}</td>
                            <td className="px-4 py-2 border-l border-gray-200 h-[52px]">{renderMinAvgMax(pass.heat, 2, 'heat')}</td>
                            <td className="px-4 py-2 border-l border-gray-200 h-[52px]">{renderMinAvgMax(pass.trueEnergy, 1, 'trueEnergy')}</td>
                            <td className="px-4 py-2 border-l border-gray-200 h-[52px]">{renderMinAvgMax(pass.oscillationWidth, 1, 'oscillationWidth')}</td>
                            <td className="px-4 py-2 border-l border-gray-200 h-[52px]">{renderMinAvgMax(pass.wireSpeed, 0, 'wireSpeed')}</td>
                          </tr>,
                          <tr key={`${pass.name}-counterclockwise-params`} className={`h-[52px] ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'} border-b`}>
                            <td className="px-4 py-2 h-[52px]">{renderMinAvgMax(pass.voltage, 1, 'voltage')}</td>
                            <td className="px-4 py-2 border-l border-gray-200 h-[52px]">{renderMinAvgMax(pass.current, 0, 'current')}</td>
                            <td className="px-4 py-2 border-l border-gray-200 h-[52px]">{renderMinAvgMax(pass.travelSpeed, 0, 'travelSpeed')}</td>
                            <td className="px-4 py-2 border-l border-gray-200 h-[52px]">{renderMinAvgMax(pass.heat, 2, 'heat')}</td>
                            <td className="px-4 py-2 border-l border-gray-200 h-[52px]">{renderMinAvgMax(pass.trueEnergy, 1, 'trueEnergy')}</td>
                            <td className="px-4 py-2 border-l border-gray-200 h-[52px]">{renderMinAvgMax(pass.oscillationWidth, 1, 'oscillationWidth')}</td>
                            <td className="px-4 py-2 border-l border-gray-200 h-[52px]">{renderMinAvgMax(pass.wireSpeed, 0, 'wireSpeed')}</td>
                          </tr>
                        ])}
                      </tbody>
                    </table>
                  </div>

                  {/* Fixed right column */}
                  <div className="sticky right-0 z-20 bg-white shadow-lg">
                    <table className="border-collapse">
                      <thead className="sticky top-0 bg-white z-10">
                        <tr className="border-b h-[72px] bg-gray-50">
                          <th className="w-[100px] px-4 text-center text-sm font-medium text-gray-600 border-l">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(weld.passes || []).flatMap((pass, index) => [
                          <tr key={`${pass.name}-clockwise-actions`} className={`h-[52px] bg-white ${index % 2 === 0 ? 'bg-opacity-100' : 'bg-opacity-50'}`}>
                            <td className="px-4 text-center border-l">
                              <Button
                                variant="ghost"
                                size="sm"
                                className="hover:bg-gray-100"
                              >
                                <ArrowRight className="h-4 w-4" />
                              </Button>
                            </td>
                          </tr>,
                          <tr key={`${pass.name}-counterclockwise-actions`} className={`h-[52px] bg-white ${index % 2 === 0 ? 'bg-opacity-100' : 'bg-opacity-50'} border-b`}>
                            <td className="px-4 text-center border-l">
                              <Button
                                variant="ghost"
                                size="sm"
                                className="hover:bg-gray-100"
                              >
                                <ArrowRight className="h-4 w-4" />
                              </Button>
                            </td>
                          </tr>
                        ])}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}; 